import { useState, type KeyboardEvent, type ReactElement } from 'react';
import { Divider, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ACTION_ORDER, cellState, has, type ActionName, type GrantState, type MatrixNode, type Preset,
} from '../../core/authorization/permissionMatrix';

interface Props {
  roots: MatrixNode[];
  /** Effective state shown in the cells: the edited grants plus dependency auto-grants. */
  state: GrantState;
  requiredBy: Record<string, string[]>;
  original: GrantState;
  edited: GrantState;
  readOnly: boolean;
  /** Full-access roles render every supported cell as granted. */
  fullAccess: boolean;
  collapsed: Set<string>;
  canManageResources: boolean;
  labelOf: (node: MatrixNode) => string;
  onToggleCollapse: (id: string) => void;
  onCell: (node: MatrixNode, action: ActionName) => void;
  onColumn: (action: ActionName) => void;
  onRowPreset: (node: MatrixNode, preset: Preset) => void;
  onEditResource: (node: MatrixNode) => void;
  onDeleteResource: (node: MatrixNode) => void;
}

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="m3.5 8.5 3 3 6-7" /></svg>
);
const DashIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M4 8h8" /></svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="3.5" y="7" width="9" height="6.5" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
  </svg>
);

export function PermissionMatrixTable(props: Props) {
  const { t } = useTranslation();
  const { roots, readOnly } = props;
  const [menu, setMenu] = useState<{ anchor: HTMLElement; node: MatrixNode } | null>(null);
  const actionLabel = (a: ActionName) => t(`permissions.actions.${a}`, { defaultValue: a[0].toUpperCase() + a.slice(1) });
  const byId = new Map<string, MatrixNode>();
  const index = (n: MatrixNode) => { byId.set(n.resourceId, n); n.children.forEach(index); };
  roots.forEach(index);

  // Arrow keys move between cells, like a spreadsheet.
  const onKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    const cell = (e.target as HTMLElement).closest<HTMLElement>('[data-cell]');
    if (!cell) return;
    const row = Number(cell.dataset.row), col = Number(cell.dataset.col);
    const [dr, dc] = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[e.key as 'ArrowUp'];
    const next = e.currentTarget.querySelector<HTMLElement>(`[data-cell][data-row="${row + dr}"][data-col="${col + dc}"]`);
    if (next) { e.preventDefault(); next.focus(); }
  };

  let rowIndex = 0;
  const renderRow = (node: MatrixNode, depth: number): ReactElement[] => {
    const r = rowIndex++;
    const isGroup = node.children.length > 0;
    const label = props.labelOf(node);
    const isCollapsed = props.collapsed.has(node.resourceId);
    const changedSelf = (props.original[node.resourceId] ?? 0) !== (props.edited[node.resourceId] ?? 0);
    const required = props.requiredBy[node.resourceId] ?? [];
    const own = props.state[node.resourceId] ?? 0;
    const supportedCount = ACTION_ORDER.filter((a) => has(node.supported, a)).length;
    const grantedCount = props.fullAccess ? supportedCount : ACTION_ORDER.filter((a) => has(own, a)).length;

    const row = (
      <tr key={node.resourceId} className={`${depth === 0 ? 'root' : 'child'} ${changedSelf ? 'changed' : ''}`}>
        <th scope="row" style={{ paddingLeft: 16 + depth * 28 }}>
          <div className="perm-mcell">
            {depth === 0 && (
              <button
                type="button"
                className={`perm-chev ${isGroup ? '' : 'none'} ${isCollapsed ? 'closed' : ''}`}
                aria-label={t('permissions.toggleSubmenus', { defaultValue: 'Show or hide submenus of {{label}}', label })}
                aria-expanded={isGroup ? !isCollapsed : undefined}
                tabIndex={isGroup ? 0 : -1}
                onClick={() => isGroup && props.onToggleCollapse(node.resourceId)}
              >▾</button>
            )}
            <span className="perm-label">{label}</span>
            {node.isSuperAdminOnly && <span className="perm-badge sys">{t('permissions.superAdminOnly', { defaultValue: 'SuperAdmin only' })}</span>}
            {node.url && <span className="perm-url">{node.url}</span>}
            {!!node.readAlso?.length && (
              <span className="perm-reads" title={t('permissions.readsTitle', { defaultValue: 'Read on this page also allows reading: {{areas}}', areas: node.readAlso.join(', ') })}>
                {t('permissions.reads', { defaultValue: 'reads' })} {node.readAlso.map((a) => a.split('.').slice(1).join('.')).join(' · ')}
              </span>
            )}
          </div>
        </th>
        {ACTION_ORDER.map((action, c) => {
          let cs = cellState(node, action, props.state);
          if (cs === 'unsupported') {
            return (
              <td key={action}>
                <span className="perm-na" title={t('permissions.unsupported', { defaultValue: '{{action}} is not available on this menu', action: actionLabel(action) })}>—</span>
              </td>
            );
          }
          if (props.fullAccess) cs = 'on';
          // Auto-granted because another granted menu needs it (R7): can't be removed here.
          const autoOnly = has(own, action) && !has(props.edited[node.resourceId] ?? 0, action);
          const depLocked = !isGroup && required.length > 0 && has(own, action) && (autoOnly || action === 'read');
          // Beyond the editing admin's own access (R8).
          const actorLocked = cs === 'locked' || (!isGroup && !has(node.grantMax, action));
          const disabled = readOnly || depLocked || actorLocked;
          const changed = !props.fullAccess && [node, ...node.children].some(
            (n) => ((props.original[n.resourceId] ?? 0) & bit(action)) !== ((props.edited[n.resourceId] ?? 0) & bit(action)),
          );
          const name = `${actionLabel(action)} ${label}`;
          const title = depLocked
            ? t('permissions.requiredBy', { defaultValue: 'Required by {{names}}', names: required.map((id) => byId.get(id) ? props.labelOf(byId.get(id)!) : id).join(', ') })
            : actorLocked
              ? t('permissions.actorLocked', { defaultValue: "You don't have {{action}} here, so you can't grant it", action: actionLabel(action) })
              : isGroup
                ? t('permissions.groupCell', { defaultValue: '{{action}} on {{label}} and its submenus', action: actionLabel(action), label })
                : name;
          return (
            <td key={action}>
              <button
                type="button"
                role="checkbox"
                aria-checked={cs === 'on' ? true : cs === 'partial' ? 'mixed' : false}
                aria-label={name}
                title={title}
                data-cell=""
                data-row={r}
                data-col={c}
                disabled={disabled}
                className={['perm-chk', cs === 'on' && 'on', cs === 'partial' && 'partial', actorLocked && 'locked', depLocked && 'dep', changed && 'changed']
                  .filter(Boolean).join(' ')}
                onClick={() => props.onCell(node, action)}
              >
                {cs === 'on' ? <CheckIcon /> : cs === 'partial' ? <DashIcon /> : actorLocked ? <LockIcon /> : null}
              </button>
            </td>
          );
        })}
        <td className="perm-sum">
          <span>{isGroup ? t('permissions.subCount', { count: node.children.length, defaultValue: '{{count}} sub' }) : `${grantedCount}/${supportedCount}`}</span>
          {(!readOnly || props.canManageResources) && (
            <button
              type="button"
              className="perm-more"
              aria-label={t('permissions.rowMenu', { defaultValue: 'More for {{label}}', label })}
              onClick={(e) => setMenu({ anchor: e.currentTarget, node })}
            >⋯</button>
          )}
        </td>
      </tr>
    );
    const children = depth === 0 && !isCollapsed ? node.children.flatMap((child) => renderRow(child, depth + 1)) : [];
    return [row, ...children];
  };

  const close = () => setMenu(null);
  const preset = (p: Preset) => { if (menu) props.onRowPreset(menu.node, p); close(); };

  return (
    <>
      <div className="perm-tablewrap">
        <table className="perm-table" onKeyDown={onKeyDown}>
          <thead>
            <tr>
              <th scope="col">{t('permissions.menu', { defaultValue: 'Menu' })}</th>
              {ACTION_ORDER.map((a) => (
                <th scope="col" key={a} className="act">
                  <button
                    type="button"
                    disabled={readOnly}
                    title={t('permissions.columnToggle', { defaultValue: 'Give or remove {{action}} for every listed menu', action: actionLabel(a) })}
                    onClick={() => props.onColumn(a)}
                  >{actionLabel(a)}</button>
                </th>
              ))}
              <th scope="col" className="sum"><span className="sr-only">{t('permissions.access', { defaultValue: 'Access' })}</span></th>
            </tr>
          </thead>
          <tbody>
            {roots.flatMap((n) => renderRow(n, 0))}
            {roots.length === 0 && (
              <tr><td colSpan={7} className="perm-empty">{t('permissions.noMenusMatch', { defaultValue: 'No menus match.' })}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={close}>
        {!readOnly && [
          <MenuItem key="r" onClick={() => preset('readOnly')}>{t('permissions.presets.readOnly', { defaultValue: 'Read only' })}</MenuItem>,
          <MenuItem key="a" onClick={() => preset('allButExecute')}>{t('permissions.presets.allButExecute', { defaultValue: 'Everything except Execute' })}</MenuItem>,
          <MenuItem key="f" onClick={() => preset('full')}>{t('permissions.presets.full', { defaultValue: 'Full (incl. Execute)' })}</MenuItem>,
          <MenuItem key="c" onClick={() => preset('clear')}>{t('permissions.presets.clear', { defaultValue: 'Clear' })}</MenuItem>,
        ]}
        {!readOnly && props.canManageResources && <Divider />}
        {props.canManageResources && [
          <MenuItem key="e" onClick={() => { if (menu) props.onEditResource(menu.node); close(); }}>{t('permissions.editResource', { defaultValue: 'Edit resource' })}</MenuItem>,
          <MenuItem key="d" sx={{ color: 'error.main' }} onClick={() => { if (menu) props.onDeleteResource(menu.node); close(); }}>{t('permissions.deleteResource', { defaultValue: 'Delete resource' })}</MenuItem>,
        ]}
      </Menu>
    </>
  );
}

const BITS: Record<ActionName, number> = { read: 1, create: 2, update: 4, delete: 8, execute: 16 };
const bit = (a: ActionName) => BITS[a];
